/**
 * ⭐ **Participações no exterior — pista a conferir, 28/09/2026.**
 *
 * > *"Precisa puxar e ir para o campo 'outros ativos'."* · *"Offshore, pode
 * > ligar no InfoSimples tbm, pois é mais 'assertivo' do que o gratuito."*
 * > — Richard
 *
 * Mora no tópico **Outros ativos**, na faixa da parte, e tem duas portas para
 * a mesma base (o ICIJ): a local, grátis, que compara o nome exato; e a da
 * Infosimples, paga e só do master, que usa a busca do site e tolera variação.
 *
 * ⛔ **Nada daqui vai ao documento**, e a tela diz isso a cada uso: base de
 * vazamento é pista, não registro, e não traz CPF nem CNPJ — o nome pode ser
 * de outra pessoa. O que o registro da jurisdição confirmar se escreve no
 * tópico. Ver `api/src/lib/exterior-do-investigado.ts`.
 */
import { useEffect, useState } from 'react';
import {
  apiInvestigados,
  type AchadoNoSiteDoIcij,
  type ExteriorDoInvestigado,
  type Investigado,
  type VinculoNoExterior,
} from '../lib/api';
import { BuscaDeAtivo } from './BuscaDeAtivo';
import type { SugestaoDeOutroAtivo } from './FichaDeOutroAtivo';

/**
 * ⭐ A jurisdição em português, para a ficha nascer como o documento escreve.
 * A base do ICIJ grava em inglês. ⚠️ É ponto de partida: quem lança confere e
 * corrige, e o que não está aqui vai como veio.
 */
const JURISDICAO: Record<string, string> = {
  'British Virgin Islands': 'Ilhas Virgens Britânicas',
  Bahamas: 'Bahamas',
  Panama: 'Panamá',
  Malta: 'Malta',
  Seychelles: 'Seychelles',
  Samoa: 'Samoa',
  Niue: 'Niue',
  Bermuda: 'Bermudas',
  'Cayman Islands': 'Ilhas Cayman',
  'Cook Islands': 'Ilhas Cook',
  Belize: 'Belize',
  'Hong Kong': 'Hong Kong',
  Cyprus: 'Chipre',
  Jersey: 'Jersey',
  Guernsey: 'Guernsey',
  'Isle of Man': 'Ilha de Man',
  Singapore: 'Singapura',
  'United Kingdom': 'Reino Unido',
  Luxembourg: 'Luxemburgo',
  Switzerland: 'Suíça',
  Uruguay: 'Uruguai',
  Mauritius: 'Maurício',
  Anguilla: 'Anguila',
  Aruba: 'Aruba',
  Barbados: 'Barbados',
  'Saint Kitts and Nevis': 'São Cristóvão e Névis',
  Nevada: 'Nevada (Estados Unidos)',
  Delaware: 'Delaware (Estados Unidos)',
  Wyoming: 'Wyoming (Estados Unidos)',
  'United States': 'Estados Unidos',
  Ireland: 'Irlanda',
  Netherlands: 'Países Baixos',
  Liechtenstein: 'Liechtenstein',
  Gibraltar: 'Gibraltar',
  Labuan: 'Labuan (Malásia)',
  Vanuatu: 'Vanuatu',
  'Marshall Islands': 'Ilhas Marshall',
  Brazil: 'Brasil',
};

/** Da pista para a ficha: o que dá para pré-preencher sem afirmar nada. */
function sugestaoDe(v: VinculoNoExterior): SugestaoDeOutroAtivo {
  const iso = (d: string | null) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : undefined);
  return {
    outro: 'participacao_exterior',
    /* ⚠️ Encerrada na base não é encerrada no registro: a sugestão só inclina, quem confere decide. */
    classificacao: v.encerradaEm ? 'exterior_encerrada' : 'exterior_vigente',
    descricao: v.sociedade,
    cidade: v.jurisdicao ? (JURISDICAO[v.jurisdicao] ?? v.jurisdicao) : undefined,
    qualidadeNoAto: v.papel === 'sociedade com o mesmo nome' || v.papel === 'a própria sociedade' ? undefined : v.papel,
    ocorridoEm: v.encerradaEm ? iso(v.encerradaEm) : (iso(v.desde) ?? iso(v.constituidaEm)),
  };
}

const emData = (d: string | null | undefined): string | null => {
  if (!d) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d.split('-').reverse().join('/') : d;
};
const noDia = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const mensagem = (e: unknown) => (e instanceof Error ? e.message : 'A busca não foi.');

/** A última busca de cada porta, relida quando a parte ou a revisão muda. */
export function usarExterior(investigadoId: string | null, revisao: number): ExteriorDoInvestigado | null {
  const [dados, setDados] = useState<ExteriorDoInvestigado | null>(null);
  useEffect(() => {
    let vivo = true;
    setDados(null);
    if (!investigadoId) return;
    apiInvestigados
      .exterior(investigadoId)
      .then((r) => vivo && setDados(r))
      .catch(() => vivo && setDados(null));
    return () => {
      vivo = false;
    };
  }, [investigadoId, revisao]);
  return dados;
}

/** O selo da faixa "Pesquisado para esta parte". */
export function SeloDoExterior({ dados }: { dados: ExteriorDoInvestigado | null }) {
  const ultima = [dados?.base, dados?.infosimples]
    .filter((b): b is NonNullable<typeof b> => !!b && b.falha === null)
    .sort((a, b) => a.em.localeCompare(b.em))
    .at(-1);
  const achou = (dados?.base?.falha === null && (dados.base.itens ?? 0) > 0) ||
    (dados?.infosimples?.falha === null && (dados.infosimples.itens ?? 0) > 0);
  return (
    <span className={`imv-fonte${ultima ? ' ok' : ' falta'}`} title="Base do ICIJ: pista a conferir, não vai ao relatório">
      <i />
      No exterior · {ultima ? `${achou ? 'há o que conferir' : 'nada no nome'} · ${noDia(ultima.em)}` : 'não buscada'}
    </span>
  );
}

export function ExteriorDaParte({
  investigado,
  dados,
  ehMaster,
  aoMudar,
  aoLancar,
}: {
  investigado: Investigado;
  dados: ExteriorDoInvestigado | null;
  ehMaster: boolean;
  /** Relê a última busca depois de buscar. */
  aoMudar: () => void;
  /** ⭐ Abre a ficha de Outros ativos já preenchida com a pista. */
  aoLancar?: (s: SugestaoDeOutroAtivo) => void;
}) {
  const [buscando, setBuscando] = useState<null | 'base' | 'infosimples'>(null);
  const [erro, setErro] = useState<{ base: string | null; infosimples: string | null }>({ base: null, infosimples: null });

  async function buscar(porta: 'base' | 'infosimples') {
    setBuscando(porta);
    setErro((e) => ({ ...e, [porta]: null }));
    try {
      if (porta === 'base') await apiInvestigados.exteriorNaBase(investigado.id);
      else await apiInvestigados.exteriorPelaInfosimples(investigado.id);
    } catch (e) {
      setErro((x) => ({ ...x, [porta]: mensagem(e) }));
    } finally {
      setBuscando(null);
      aoMudar();
    }
  }

  return (
    <div className="imv-painel">
      <p className="imv-painel-titulo">Participações no exterior · {investigado.nome}</p>
      <p className="ajuda">
        <strong>Pista, não prova.</strong> A base do ICIJ reúne documentos vazados de escritórios que abrem sociedades
        no exterior; não é registro oficial e não traz CPF nem CNPJ, então o mesmo nome pode ser de outra pessoa.{' '}
        <strong>Nada daqui vai ao relatório.</strong> O que o registro da jurisdição confirmar se lança em Outros ativos,
        com o registro que conferiu: o botão ao lado de cada vínculo abre a ficha já preenchida.
      </p>

      <div className="ext-portas">
        <BuscaDeAtivo
          rotulo="Buscar na base do ICIJ"
          alcance={
            dados?.baseDe
              ? `Compara o nome exato, sem acento e sem pontuação, na base que mora no servidor (publicada pelo ICIJ em ${emData(dados.baseDe)}). O nome não sai do Hub.`
              : 'A base do ICIJ ainda não foi trazida para este servidor.'
          }
          custo={{ tipo: 'gratuita' }}
          buscando={buscando === 'base'}
          erro={erro.base}
          aoBuscar={() => void buscar('base')}
        />
        {ehMaster && (
          <BuscaDeAtivo
            rotulo="Buscar pela Infosimples"
            unidade={['nome', 'nomes']}
            alcance="Usa a busca do site do ICIJ, que acha também grafias parecidas, e traz os 100 primeiros resultados. O que ela acha é detalhado pela base do servidor."
            custo={{ tipo: 'paga', quantas: 1, unitario: dados?.preco ?? null, aproximado: true }}
            avisos={
              <p className="ajuda">
                ⚠️ O nome de {investigado.nome} vai à Infosimples, empresa privada sob contrato. É a exceção decidida em
                28/09/2026 à regra de 19/09.
              </p>
            }
            buscando={buscando === 'infosimples'}
            erro={erro.infosimples}
            aoBuscar={() => void buscar('infosimples')}
          />
        )}
      </div>

      {dados?.base && <ResultadoDaBase b={dados.base} aoLancar={aoLancar} />}
      {dados?.infosimples && <ResultadoDaInfosimples b={dados.infosimples} aoLancar={aoLancar} />}
    </div>
  );
}

type AoLancar = ((s: SugestaoDeOutroAtivo) => void) | undefined;

function Vinculo({ v, aoLancar }: { v: VinculoNoExterior; aoLancar: AoLancar }) {
  const periodo = [emData(v.desde), emData(v.ate)].filter(Boolean).join(' a ');
  const detalhes = [
    v.papel,
    periodo || null,
    v.constituidaEm ? `constituída em ${emData(v.constituidaEm)}` : null,
    v.encerradaEm ? `encerrada em ${emData(v.encerradaEm)}` : null,
    v.situacao,
    v.conjunto,
  ].filter(Boolean);
  return (
    <li className="ext-vinculo">
      <span className="ext-sociedade">
        <b>{v.sociedade}</b>
        {v.jurisdicao && <span> · {v.jurisdicao}</span>}
      </span>
      <span className="ext-detalhe">{detalhes.join(' · ')}</span>
      <span className="ext-na-base">
        Na base: {v.nomeNaBase}
        {v.paisesDaPessoa ? ` · ${v.paisesDaPessoa}` : ''}
      </span>
      {aoLancar && (
        <button type="button" className="imv-bt fantasma p ext-lancar" onClick={() => aoLancar(sugestaoDe(v))}>
          Lançar em Outros ativos
        </button>
      )}
    </li>
  );
}

function ResultadoDaBase({ b, aoLancar }: { b: NonNullable<ExteriorDoInvestigado['base']>; aoLancar: AoLancar }) {
  return (
    <div className="ext-resultado">
      <p className="ext-cab">
        Base do ICIJ · nome exato · buscada em {noDia(b.em)} ·{' '}
        {b.falha ? 'não respondeu' : `${b.itens} vínculo${b.itens === 1 ? '' : 's'}`}
      </p>
      {b.falha ? (
        <p className="erro">{b.falha}</p>
      ) : b.lista.length === 0 ? (
        <p className="ajuda">
          Nenhum vínculo com o nome exato. Não quer dizer que não haja: a base é um recorte de vazamentos, e a grafia
          pode variar.
        </p>
      ) : (
        <ul className="ext-lista">
          {b.lista.map((v, i) => (
            <Vinculo key={`${v.no}-${i}`} v={v} aoLancar={aoLancar} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ResultadoDaInfosimples({ b, aoLancar }: { b: NonNullable<ExteriorDoInvestigado['infosimples']>; aoLancar: AoLancar }) {
  return (
    <div className="ext-resultado">
      <p className="ext-cab">
        Infosimples · busca do site · feita em {noDia(b.em)} ·{' '}
        {b.falha ? 'não respondeu' : `${b.itens} resultado${b.itens === 1 ? '' : 's'}`}
      </p>
      {b.falha ? (
        <p className="erro">{b.falha}</p>
      ) : b.lista.length === 0 ? (
        <p className="ajuda">A busca do site não trouxe resultado para este nome.</p>
      ) : (
        <ul className="ext-lista">
          {b.lista.map((a: AchadoNoSiteDoIcij, i) => (
            <li key={`${a.no ?? 'sem'}-${i}`} className="ext-vinculo">
              <span className="ext-sociedade">
                <b>{a.descricao}</b>
                {a.pais && <span> · {a.pais}</span>}
              </span>
              {a.conjunto && <span className="ext-detalhe">{a.conjunto}</span>}
              {a.vinculos.length > 0 && (
                <ul className="ext-lista ext-dentro">
                  {a.vinculos.map((v, k) => (
                    <Vinculo key={`${v.no}-${k}`} v={v} aoLancar={aoLancar} />
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
